import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  VStack,
  useDisclosure,
  Badge,
  Box,
  Tag,
  Spacer,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Grid,
} from '@chakra-ui/react';
import FormRow from '@/features/user/FormRow.jsx';
import { useAddAuthUserMutation, useGetRoutesQuery, useUpdateAuthUserMutation, useUpdateAuthUserPasswordMutation } from '@/app/services/nucleus';
import AlertComponent from '../common/AlertComponent';
import { BsPersonFillX, BsTrash } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectRolesList } from '../enum/enumSlice';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import PasswordInput from '../common/PasswordInput';
import { parseErrorLocation, passwordRules } from './userHelper';
import { FaCheckCircle } from 'react-icons/fa';
import { FaCircleXmark } from 'react-icons/fa6';
import ComplexityRules from '../common/ComplexityRules';
import LoaderOverlay from '../common/LoaderOverlay';
import ChangePasswordModal from './ChangePasswordModal';
import { SingleValueName } from '@/features/common/select/SingleValue.jsx';
import { OptionRoutes } from '@/features/common/select/Option.jsx';
import TableButton from '@/features/common/table/TableButton.jsx';
import TableFooter from '@/features/common/table/TableFooter.jsx';
import useTable from '@/hooks/useTable.js';
import { isArrayValue } from '@/utils/helpers';

const NewUserForm = ({ isNew, user, onSave }) => {
  const navigate = useNavigate();
  const changePasswordModal = useDisclosure();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [routes, setRoutes] = useState([]);
  const [admin, setAdmin] = useState(false);
  const [page, setPage] = useState(1);
  const roles = useSelector(selectRolesList);
  const [fieldValidation, setFieldValidation] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState(false);
  const [typingPassword, setTypingPassword] = useState(false);
  const [showComplexityRules, setShowComplexityRules] = useState(false);
  const [submitForm, setSubmitForm] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

  const { slice, range } = useTable(routes, page, 6);

  const [addAuthUser, { isLoading: addAuthUserLoading, isError: addAuthUserError, error: addAuthUserErrorMsg, reset: addAuthUserReset }] =
    useAddAuthUserMutation();

  const [
    updateAuthUser,
    { isLoading: updateAuthUserLoading, isError: updateAuthUserError, error: updateAuthUserErrorMsg, reset: updateAuthUserReset },
  ] = useUpdateAuthUserMutation();

  const [
    updateAuthUserPassword,
    {
      isLoading: updateAuthUserPasswordLoading,
      isSuccess: updateAuthUserPasswordSuccess,
      isError: updateAuthUserPasswordError,
      error: updateAuthUserPasswordErrorMsg,
      reset: updateAuthUserPasswordReset,
    },
  ] = useUpdateAuthUserPasswordMutation();

  const { data: routeData, isLoading: isRoutesLoading, isSuccess: isRoutesSuccess, isError: isRoutesError, error: routesError } = useGetRoutesQuery();

  const rulesData = useMemo(() => passwordRules(password), [password]);

  const icons = {
    validIcon: FaCheckCircle,
    invalidIcon: FaCircleXmark,
    validAriaLabel: 'check-icon',
    invalidAriaLabel: 'x-icon',
  };

  useEffect(() => {
    setFieldValidation({
      first_name: {
        valid: true,
        error: '',
      },
      last_name: {
        valid: true,
        error: '',
      },
      email: {
        valid: true,
        error: '',
      },
      username: {
        valid: true,
        error: '',
      },
      password: {
        valid: true,
        error: '',
      },
    });
    addAuthUserReset();
  }, []);

  useEffect(() => {
    const isValid = rulesData.every((val) => val.value === true);
    setPasswordValidation(isValid);
    setSubmitButtonDisabled(!isValid);
  }, [rulesData]);

  useEffect(() => {
    if (user?.id) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setEmail(user.email);
      setUsername(user.username);
      setRole(user.roles.map((r) => ({ label: r, value: r })));
      setAdmin(user.admin);
      setSubmitButtonDisabled(false);
    }
  }, [user]);

  useEffect(() => {
    if (isRoutesSuccess && user?.id) {
      const userRoutes = user?.allowed_routes;
      // NOTE: routeData has nested options array, but the select component only accepts a flat array for it's value prop
      if (isArrayValue(userRoutes)) {
        const selectedRoutes = routeData.reduce((prev, cur) => {
          const routes = cur.options.filter((r) => userRoutes.includes(r.id));
          if (isArrayValue(routes)) {
            return [...prev, ...routes];
          }
          return prev;
        }, []);
        setRoutes(selectedRoutes);
      }
    }
  }, [user, isRoutesSuccess]);

  useEffect(() => {
    if (addAuthUserError) {
      const errorLocation = parseErrorLocation(addAuthUserErrorMsg, fieldValidation);
      setFieldValidation(errorLocation);
    }
  }, [addAuthUserError]);

  useEffect(() => {
    if (updateAuthUserError) {
      const errorLocation = parseErrorLocation(updateAuthUserErrorMsg, fieldValidation);
      setFieldValidation(errorLocation);
    }
  }, [updateAuthUserError]);

  useEffect(() => {
    if (submitForm) {
      const allFieldsValid = Object.values(fieldValidation).every((val) => val.valid);
      setSubmitButtonDisabled(!allFieldsValid);
    }
  }, [fieldValidation, submitForm]);

  const handleFirstName = (e) => {
    const val = e.target.value;
    setFirstName(val);
    handleFieldChange('first_name');
  };

  const handleLastName = (e) => {
    const val = e.target.value;
    setLastName(val);
    handleFieldChange('last_name');
  };

  const handleEmail = (e) => {
    const val = e.target.value;
    setEmail(val);
    handleFieldChange('email');
  };

  const handleUsername = (e) => {
    if (isNew) {
      const val = e.target.value;
      setUsername(val);
      handleFieldChange('username');
    } else {
      e.preventDefault();
    }
  };

  const handlePassword = (e) => {
    setTypingPassword(true);
    const val = e.target.value;
    setShowComplexityRules(Boolean(val));
    setPassword(val);
  };

  const handleAdmin = (value) => {
    setAdmin(value === 'true');
  };

  const handleRoles = (selectOptions) => {
    setRole(selectOptions || []);
  };

  const handleFieldChange = (fieldName) => {
    setFieldValidation((prev) => ({
      ...prev,
      [fieldName]: {
        valid: true,
        error: '',
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addAuthUserReset();
    updateAuthUserReset();
    setSubmitForm(true);

    const routeIds = isArrayValue(routes) ? routes.reduce((prev, cur) => [...prev, cur.id], []) : [];

    const authUserPayload = {
      id: user.id,
      first_name: firstName,
      last_name: lastName,
      admin,
      email,
      roles: role.map((r) => r.value),
      allowed_routes: routeIds,
      username,
    };

    if (isNew) {
      addAuthUser({
        authUser: { ...authUserPayload, password },
      })
        .unwrap()
        .then((payload) => {
          onSave(payload);
          navigate('/user_management');
        })

        .catch((error) => {
          console.log(error);
        });
    } else {
      updateAuthUser({ id: user.id, authUser: authUserPayload })
        .unwrap()
        .then((payload) => {
          onSave(payload);
          navigate('/user_management');
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleChangePassword = ({ password, confirmPassword }) => {
    updateAuthUserPassword({ id: user.id, password, confirmPassword })
      .unwrap()
      .then((payload) => {
        if (payload) {
          changePasswordModal.onClose();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleRoutes = (item, { action }) => {
    setRoutes(item);
  };

  const formatGroupLabel = (data) => {
    const tags = data.label.split(',');
    const colors = ['cyan', 'purple'];
    return (
      <HStack gap={4} justifyContent="space-between">
        {tags.map((tag, index) => (
          <Tag key={tag} colorScheme={colors[index]} px={3}>
            {tag}
          </Tag>
        ))}
        <Spacer />
        <Badge>{data.options.length}</Badge>
      </HStack>
    );
  };

  const handleRemoveRouteAction = (e, val) => {
    e.preventDefault();
    const reducedRoutes = routes.filter((item) => item.id !== val.id);
    setRoutes(reducedRoutes);
    // setShowError(false);
  };

  const routesPlaceholder = (routeLength, isLoading) => {
    if (isLoading) {
      return 'Loading routes...';
    } else if (routeLength > 0) {
      return `Select routes  +${routeLength}`;
    } else {
      return 'Select routes';
    }
  };

  return (
    <form aria-label={isNew ? 'New User' : 'Edit User'} name="user-form" onSubmit={handleSubmit}>
      <VStack spacing={4} alignItems="flex-start" width="full">
        <LoaderOverlay loading={addAuthUserLoading || updateAuthUserLoading} />

        {addAuthUserError && (
          <AlertComponent
            icon={<BsPersonFillX style={{ height: '24px', width: '24px' }} />}
            status="error"
            title="Error Adding User"
            description={addAuthUserErrorMsg?.status === 422 ? 'Please add a valid user' : addAuthUserErrorMsg?.data?.detail}
          />
        )}

        {isRoutesError && (
          <AlertComponent
            icon={<BsPersonFillX style={{ height: '24px', width: '24px' }} />}
            status="error"
            title="Error fetching routes"
            description={routesError?.data?.detail}
          />
        )}

        <FormControl id="firstName" isInvalid={fieldValidation && !fieldValidation['first_name'].valid} aria-label="first-name-control">
          <FormRow id="firstName" label="First Name">
            <Input id="firstName" name="firstName" type="text" value={firstName} onChange={handleFirstName} />
            <FormErrorMessage gridColumn="2" mt="-10px">
              {fieldValidation && fieldValidation['first_name'].error}
            </FormErrorMessage>
          </FormRow>
        </FormControl>

        <FormControl id="lastName" isInvalid={fieldValidation && !fieldValidation['last_name'].valid} aria-label="last-name-control">
          <FormRow id="lastName" label="Last Name">
            <Input id="lastName" name="lastName" type="text" value={lastName} onChange={handleLastName} />
            <FormErrorMessage gridColumn="2" mt="-10px">
              {fieldValidation && fieldValidation['last_name'].error}
            </FormErrorMessage>
          </FormRow>
        </FormControl>

        <FormControl id="email" isInvalid={fieldValidation && !fieldValidation['email'].valid} aria-label="email-control">
          <FormRow id="email" label="Email">
            <Input id="email" name="email" type="email" value={email} onChange={handleEmail} />
            <FormErrorMessage gridColumn="2" mt="-10px">
              {fieldValidation && fieldValidation['email'].error}
            </FormErrorMessage>
          </FormRow>
        </FormControl>

        <FormControl id="role" aria-label="role-control">
          <FormRow id="role" label="Role">
            <Select
              id="role"
              aria-label="role"
              focusBorderColor="brand.700"
              chakraStyles={chakraStyles}
              options={roles}
              isMulti
              onChange={handleRoles}
              value={role}
              colorScheme="cyan"
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => option.value}
              placeholder="Select a role"
            />
          </FormRow>
        </FormControl>

        <FormControl id="admin-control" aria-label="admin-control">
          <FormRow id="admin" label="Admin">
            <RadioGroup id="admin" aria-labelledby="admin" onChange={handleAdmin} value={admin.toString()} defaultValue="false">
              <Stack direction="row">
                <Radio value="true">True</Radio>
                <Radio value="false">False</Radio>
              </Stack>
            </RadioGroup>
          </FormRow>
        </FormControl>

        <FormControl id="routes-control" aria-label="routes-control">
          <FormRow id="routes" label="Routes">
            <Select
              chakraStyles={chakraStyles}
              focusBorderColor="brand.700"
              isMulti={true}
              aria-label="routes"
              options={routeData}
              isLoading={isRoutesLoading}
              isDisabled={isRoutesLoading}
              formatGroupLabel={formatGroupLabel}
              isSearchable
              value={routes}
              isClearable={true}
              onChange={handleRoutes}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              placeholder={routesPlaceholder(routes?.length, isRoutesLoading)}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              components={{ SingleValue: SingleValueName, Option: OptionRoutes }}
            />
          </FormRow>

          <Grid
            templateColumns={{
              base: '1fr',
              md: '1fr 4fr',
            }}
            gap={4}
            alignItems="center"
            width={{
              base: '100%',
              md: '90%',
            }}
          >
            <Spacer />

            {routes && routes.length > 0 && (
              <Box borderWidth="1px" borderRadius="lg" overflow="hidden" mt="5">
                <TableContainer pt="4">
                  <Table variant="striped" size="sm" aria-label="routes selection" colorScheme={'lightgray'}>
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Path</Th>
                        {!isNew ? null : <Th>Action</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {slice.map((val) => (
                        <Tr key={val.id}>
                          <Td>{val.name}</Td>
                          <Td>{val.path}</Td>
                          <Td>
                            <TableButton
                              color="cyan"
                              aria-label={`remove-${val.name}`}
                              icon={<BsTrash />}
                              onClick={(e) => handleRemoveRouteAction(e, val)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
                {range && range.length > 1 && (
                  <Box width="100%" p={3}>
                    <TableFooter range={range} slice={slice} setPage={setPage} page={page} />
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </FormControl>

        <FormControl id="username" aria-label="username-control" isInvalid={fieldValidation && !fieldValidation['username'].valid}>
          <FormRow id="username" label="Username">
            <Input id="username" name="username" type="text" value={username} onChange={handleUsername} isDisabled={!isNew} />
            <FormErrorMessage gridColumn="2" mt="-10px">
              {fieldValidation && fieldValidation['username'].error}
            </FormErrorMessage>
          </FormRow>
        </FormControl>

        {isNew && (
          <FormControl
            id="password"
            aria-label="password-control"
            isInvalid={typingPassword && !passwordValidation && fieldValidation && !fieldValidation['password'].valid}
          >
            <FormRow id="password" label="Password">
              <PasswordInput label="password" value={password} handleChange={handlePassword} autoComplete="new-password" />
              <FormErrorMessage gridColumn="2" mt="-10px">
                {fieldValidation && fieldValidation['password'].error}
              </FormErrorMessage>
              {showComplexityRules && (
                <ComplexityRules rulesData={rulesData} icons={icons} styleProps={{ marginTop: '-8px', marginLeft: '8px', gridColumn: '2' }} />
              )}
            </FormRow>
          </FormControl>
        )}

        <HStack gap={4} mt={8}>
          <Button variant="purple" isDisabled={submitButtonDisabled} type="submit">
            {isNew ? 'Submit' : 'Save'}
          </Button>
          {!isNew && (
            <Button variant="cyan" onClick={changePasswordModal.onOpen}>
              Change Password
            </Button>
          )}
        </HStack>
      </VStack>
      {!isNew && (
        <ChangePasswordModal
          onClose={changePasswordModal.onClose}
          isOpen={changePasswordModal.isOpen}
          onOpen={changePasswordModal.onOpen}
          onSubmit={handleChangePassword}
          request={{
            isLoading: updateAuthUserPasswordLoading,
            isError: updateAuthUserPasswordError,
            error: updateAuthUserPasswordErrorMsg,
            isSuccess: updateAuthUserPasswordSuccess,
            reset: updateAuthUserPasswordReset,
          }}
        />
      )}
    </form>
  );
};

NewUserForm.propTypes = {
  user: PropTypes.object,
  isNew: PropTypes.bool,
  onSave: PropTypes.func,
};

export default NewUserForm;
